// AIDialerCampaignCreate.jsx — multi-step wizard for creating a v1 campaign.
//
// Steps: Persona → Lead Source → Schedule + Compliance → Budget →
//        Pacing + Hand-off → Review + Launch.
//
// Posts to /api/dialer/v1/campaigns and optionally /campaigns/:id/start.
// All routes are gated behind VITE_ENABLE_AI_DIALER (App.jsx redirects).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  ChevronLeft, ChevronRight, Check, Loader2, Bot, Users, Clock,
  DollarSign, GitBranch, FileText, Save, Play, Lock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import TopBar from '../components/TopBar.jsx';
import CostBadge from '../components/dialer/CostBadge.jsx';
import { apiGet, apiPost } from '../lib/api.js';

const STEPS = [
  { id: 1, label: 'Persona',     icon: Bot },
  { id: 2, label: 'Lead Source', icon: Users },
  { id: 3, label: 'Schedule',    icon: Clock },
  { id: 4, label: 'Budget',      icon: DollarSign },
  { id: 5, label: 'Pacing',      icon: GitBranch },
  { id: 6, label: 'Review',      icon: FileText },
];

const STOCK_VOICES = [
  { id: 'pNInz6obpgDQGcFmaJgB', label: 'Adam (warm, male)' },
  { id: 'EXAVITQu4vr4xnSDxMaL', label: 'Sarah (friendly, female)' },
  { id: 'CYw3kZ02Hs0563khs1Fj', label: 'Dave (calm, male)' },
  { id: 'XrExE9yKIg1WjnnlVkGX', label: 'Matilda (energetic, female)' },
];

const PREBUILT_PERSONAS = [
  {
    key: 'cold-warm-sdr',
    name: 'Cold-Warm SDR',
    voiceId: 'pNInz6obpgDQGcFmaJgB',
    openingLine: "Hi, this is Adam — I'm following up on the life insurance quote you requested. Quick question…",
    qualifyingQuestions: [
      'Are you still looking for coverage?',
      'What state are you in?',
      'Any major health conditions in the last 2 years?',
    ],
    closingScript: 'Great — let me transfer you to a licensed agent right now so we can lock in a rate.',
  },
  {
    key: 'friendly-qualifier',
    name: 'Friendly Qualifier',
    voiceId: 'EXAVITQu4vr4xnSDxMaL',
    openingLine: "Hey there! This is Sarah from CIVIX — you requested a final expense quote a bit ago, do you have a quick second?",
    qualifyingQuestions: [
      'Are you the decision-maker on coverage?',
      'What is your age and state?',
      'Have you been declined coverage before?',
    ],
    closingScript: "Perfect — I'm bringing one of our licensed agents on the line right now.",
  },
];

function StepBadge({ step, active, complete, onClick }) {
  const Icon = step.icon;
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
        active
          ? 'bg-civix-purple/20 text-white border border-civix-purple/40'
          : complete
          ? 'bg-green-500/10 text-green-300 border border-green-500/30'
          : 'text-white/40 border border-transparent hover:text-white/70'
      }`}
    >
      {complete ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
      <span className="hidden md:inline">{step.label}</span>
    </button>
  );
}

export default function AIDialerCampaignCreate() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [nameTouched, setNameTouched] = useState(false);
  const [namePulse, setNamePulse] = useState(false);
  const nameInputRef = useRef(null);
  const [form, setForm] = useState({
    name: '',
    persona: {
      name: 'Cold-Warm SDR',
      voiceId: 'pNInz6obpgDQGcFmaJgB',
      systemPrompt: '',
      qualifyingQuestions: PREBUILT_PERSONAS[0].qualifyingQuestions,
      closingScript: PREBUILT_PERSONAS[0].closingScript,
    },
    leadSource: { type: 'list', leadIds: [], filter: {} },
    schedule: {
      timezone: 'America/Denver',
      hours: { start: '09:00', end: '20:00' },
      daysOfWeek: [1, 2, 3, 4, 5],
      respectTcpa: true,
    },
    budget: { perCallCapUsd: 2, dailyCapUsd: 50 },
    handoff: { mode: 'warm_transfer_then_book', transferTo: [], transferTimeoutSec: 25, appointmentCalendarId: '' },
    pacing: { parallelLines: 3 },
  });

  // Load bots, leads (limited), teammates for transferTo picker.
  const { data: botsData } = useQuery({
    queryKey: ['dialer-v1-bots'],
    queryFn:  () => apiGet('/dialer/v1/bots'),
  });
  const bots = botsData?.data || [];

  const { data: leadsData } = useQuery({
    queryKey: ['leads', 'dialer-picker'],
    queryFn:  () => apiGet('/leads', { limit: 200 }),
  });
  const leads = leadsData?.data || leadsData?.leads || [];

  const { data: teamData } = useQuery({
    queryKey: ['agents'],
    queryFn:  () => apiGet('/agents'),
  });
  const team = teamData?.data || teamData?.agents || [];

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updatePersona = (patch) => setForm((f) => ({ ...f, persona: { ...f.persona, ...patch } }));

  // Apply a prebuilt persona to the form.
  const usePrebuilt = (p) => {
    updatePersona({
      name: p.name,
      voiceId: p.voiceId,
      qualifyingQuestions: p.qualifyingQuestions,
      closingScript: p.closingScript,
    });
  };

  // Apply a saved trained bot to the form (snapshots its persona fields).
  const useBot = (b) => {
    updatePersona({
      name: b.name,
      voiceId: b.voiceId || form.persona.voiceId,
      qualifyingQuestions: b.qualifyingQuestions || [],
      closingScript: b.closingScript || '',
      systemPrompt: b.personalityNotes || '',
    });
  };

  useEffect(() => {
    if (step === 6) {
      setNameTouched(true);
      const id = setTimeout(() => nameInputRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [step]);

  const nameMissing = !form.name.trim();
  const nudgeNameField = () => {
    setNameTouched(true);
    if (nameInputRef.current) {
      nameInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      nameInputRef.current.focus();
    }
    setNamePulse(true);
    setTimeout(() => setNamePulse(false), 900);
  };

  const validStep = useMemo(() => {
    switch (step) {
      case 1: return !!form.persona.name && !!form.persona.voiceId;
      case 2:
        if (form.leadSource.type === 'list') return (form.leadSource.leadIds || []).length > 0;
        if (form.leadSource.type === 'filter') return true;
        if (form.leadSource.type === 'csv') return (form.leadSource.leadIds || []).length > 0;
        return false;
      case 3: return !!form.schedule.hours?.start && !!form.schedule.hours?.end && (form.schedule.daysOfWeek || []).length > 0;
      case 4: return form.budget.perCallCapUsd > 0 && form.budget.dailyCapUsd > 0;
      case 5: return !!form.handoff.mode && form.pacing.parallelLines >= 1;
      case 6: return !!form.name.trim();
      default: return false;
    }
  }, [step, form]);

  const createMutation = useMutation({
    mutationFn: (launch) => apiPost('/dialer/v1/campaigns', {
      name: form.name,
      persona: form.persona,
      leadSource: form.leadSource,
      schedule: form.schedule,
      budget: form.budget,
      handoff: form.handoff,
    }).then(async (r) => {
      const c = r?.data || r;
      if (launch && c?._id) {
        await apiPost(`/dialer/v1/campaigns/${c._id}/start`, {});
      }
      return c;
    }),
    onSuccess: () => {
      toast.success('Campaign saved');
      nav('/ai-dialer?tab=campaigns');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });

  return (
    <div className="space-y-4">
      <TopBar
        title="New AI Dialer Campaign"
        subtitle="Configure persona, leads, schedule, budget, and hand-off. Save as draft or launch when ready."
      />
      <div className="panel p-4">
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {STEPS.map((s) => (
            <React.Fragment key={s.id}>
              <StepBadge step={s} active={step === s.id} complete={step > s.id} onClick={() => setStep(s.id)} />
              {s.id < STEPS.length && <ChevronRight className="w-3 h-3 text-white/30 shrink-0" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="panel p-6 min-h-[420px]">
        {step === 1 && (
          <Step1Persona
            form={form}
            bots={bots}
            usePrebuilt={usePrebuilt}
            useBot={useBot}
            updatePersona={updatePersona}
          />
        )}
        {step === 2 && (
          <Step2LeadSource form={form} leads={leads} update={update} />
        )}
        {step === 3 && (
          <Step3Schedule form={form} update={update} />
        )}
        {step === 4 && (
          <Step4Budget form={form} update={update} />
        )}
        {step === 5 && (
          <Step5Pacing form={form} team={team} update={update} />
        )}
        {step === 6 && (
          <Step6Review
            form={form}
            update={update}
            nameInputRef={nameInputRef}
            nameError={nameTouched && nameMissing}
            namePulse={namePulse}
          />
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1}
          className="btn-ghost flex items-center gap-2 disabled:opacity-30"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>

        {step < STEPS.length ? (
          <button
            onClick={() => setStep((s) => Math.min(STEPS.length, s + 1))}
            disabled={!validStep}
            className="btn-primary flex items-center gap-2 disabled:opacity-40"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <div className="flex items-center gap-2">
            {!validStep && (
              <span className="text-xs text-red-300 mr-2">Add a campaign name to enable Save.</span>
            )}
            <span
              onClick={() => { if (!validStep) nudgeNameField(); }}
              onMouseEnter={() => { if (!validStep) setNameTouched(true); }}
              className={!validStep ? 'inline-block cursor-not-allowed' : 'inline-block'}
            >
              <button
                onClick={() => createMutation.mutate(false)}
                disabled={!validStep || createMutation.isPending}
                className="btn-ghost flex items-center gap-2"
                title={!validStep ? 'Add a campaign name first' : 'Save without dialing — you can launch later'}
              >
                {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Draft
              </button>
            </span>
            <span
              onClick={() => { if (!validStep) nudgeNameField(); }}
              onMouseEnter={() => { if (!validStep) setNameTouched(true); }}
              className={!validStep ? 'inline-block cursor-not-allowed' : 'inline-block'}
            >
              <button
                onClick={() => createMutation.mutate(true)}
                disabled={!validStep || createMutation.isPending}
                className="btn-primary flex items-center gap-2"
                title={!validStep ? 'Add a campaign name first' : 'Save and start dialing right away'}
              >
                {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Save &amp; Launch
              </button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function Step1Persona({ form, bots, usePrebuilt, useBot, updatePersona }) {
  const [mode, setMode] = useState('prebuilt'); // prebuilt | bot | custom
  return (
    <div className="space-y-5">
      <div>
        <div className="text-sm font-semibold text-white mb-1">Persona</div>
        <div className="text-xs text-white/50">
          Pick a pre-built screener, choose a bot you've trained, or roll your own.
        </div>
      </div>

      <div className="flex gap-2">
        {['prebuilt', 'bot', 'custom'].map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${mode === m ? 'bg-civix-purple/20 text-white border border-civix-purple/40' : 'text-white/50 border border-white/10 hover:bg-white/5'}`}
          >
            {m === 'prebuilt' ? 'Pre-built' : m === 'bot' ? 'My trained bots' : 'Custom'}
          </button>
        ))}
      </div>

      {mode === 'prebuilt' && (
        <div className="grid md:grid-cols-2 gap-3">
          {PREBUILT_PERSONAS.map((p) => (
            <button
              key={p.key}
              onClick={() => usePrebuilt(p)}
              className={`panel p-4 text-left hover:border-civix-purple/40 transition-all ${form.persona.name === p.name ? 'border-civix-purple/40' : ''}`}
            >
              <div className="text-sm font-semibold text-white mb-1">{p.name}</div>
              <div className="text-xs text-white/50 line-clamp-3">{p.openingLine}</div>
            </button>
          ))}
        </div>
      )}

      {mode === 'bot' && (
        <div>
          {bots.length === 0 ? (
            <div className="panel p-6 text-center text-sm text-white/60">
              No trained bots yet.{' '}
              <Link to="/ai-dialer/bots/train" className="text-civix-cyan hover:underline">
                Train a bot →
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {bots.map((b) => (
                <button key={b._id} onClick={() => useBot(b)} className="panel p-4 text-left hover:border-civix-purple/40">
                  <div className="text-sm font-semibold text-white">{b.name}</div>
                  <div className="text-xs text-white/50 line-clamp-2">{b.description || '—'}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {mode === 'custom' && (
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Persona name</label>
            <input
              className="input w-full"
              value={form.persona.name}
              onChange={(e) => updatePersona({ name: e.target.value })}
              placeholder="e.g. Senior Final Expense Closer"
            />
          </div>
          <div>
            <label className="form-label">Voice</label>
            <select
              className="input w-full"
              value={form.persona.voiceId}
              onChange={(e) => updatePersona({ voiceId: e.target.value })}
            >
              {STOCK_VOICES.map((v) => (
                <option key={v.id} value={v.id}>{v.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="form-label">Personality / system prompt</label>
            <textarea
              className="input w-full"
              rows={4}
              value={form.persona.systemPrompt}
              onChange={(e) => updatePersona({ systemPrompt: e.target.value })}
              placeholder="Warm but direct. Reference the lead's quote request within the first 10 seconds."
            />
          </div>
        </div>
      )}

      <div className="panel p-4 bg-white/5">
        <div className="text-xs text-white/40 uppercase tracking-wider mb-2">Snapshot</div>
        <div className="text-sm text-white/80">{form.persona.name}</div>
        <div className="text-xs text-white/50 mt-1">Voice: {STOCK_VOICES.find((v) => v.id === form.persona.voiceId)?.label || form.persona.voiceId}</div>
        <div className="text-xs text-white/50 mt-1">{form.persona.qualifyingQuestions?.length || 0} qualifying questions</div>
      </div>
    </div>
  );
}

function Step2LeadSource({ form, leads, update }) {
  const ls = form.leadSource;
  const setType = (type) => update({ leadSource: { ...ls, type } });
  const toggleLead = (id) => {
    const cur = new Set(ls.leadIds || []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    update({ leadSource: { ...ls, leadIds: Array.from(cur) } });
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm font-semibold text-white mb-1">Lead Source</div>
        <div className="text-xs text-white/50">Where to pull leads from.</div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'list',   label: 'Pick from list' },
          { id: 'filter', label: 'Filter existing' },
          { id: 'csv',    label: 'CSV upload' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setType(t.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${ls.type === t.id ? 'bg-civix-purple/20 text-white border border-civix-purple/40' : 'text-white/50 border border-white/10 hover:bg-white/5'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {ls.type === 'list' && (
        <div className="panel p-3 max-h-[340px] overflow-y-auto">
          {leads.length === 0 && (
            <div className="text-center py-6">
              <Users className="w-8 h-8 text-white/20 mx-auto mb-2" />
              <div className="text-sm font-medium text-white/70 mb-1">No leads in your workspace yet</div>
              <Link to="/leads/import" className="text-xs text-civix-cyan hover:underline">
                Import leads to get started →
              </Link>
            </div>
          )}
          {leads.slice(0, 200).map((l) => {
            const sel = (ls.leadIds || []).includes(l._id);
            return (
              <label key={l._id} className="flex items-center gap-3 py-1.5 px-2 rounded-sm hover:bg-white/5 cursor-pointer text-sm">
                <input type="checkbox" checked={sel} onChange={() => toggleLead(l._id)} />
                <span className="text-white/80">{l.firstName} {l.lastName}</span>
                <span className="text-white/40 text-xs">{l.state || ''} · {l.phone || ''}</span>
              </label>
            );
          })}
        </div>
      )}

      {ls.type === 'filter' && (
        <div className="panel p-4 text-sm text-white/60 space-y-3">
          <div>
            <label className="form-label">State</label>
            <input
              className="input w-full"
              placeholder="e.g. TX,AZ,NV"
              value={(ls.filter?.state || []).join(',')}
              onChange={(e) => update({ leadSource: { ...ls, filter: { ...ls.filter, state: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) } } })}
            />
          </div>
          <div>
            <label className="form-label">Status</label>
            <input
              className="input w-full"
              placeholder="e.g. new,working"
              value={(ls.filter?.status || []).join(',')}
              onChange={(e) => update({ leadSource: { ...ls, filter: { ...ls.filter, status: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) } } })}
            />
          </div>
        </div>
      )}

      {ls.type === 'csv' && (
        <div className="panel p-6 text-center text-sm text-white/60">
          Use the existing{' '}
          <Link to="/leads/import" className="text-civix-cyan hover:underline">Lead Import</Link>
          {' '}to bring leads in, then tag them and come back here to pick the list or filter.
        </div>
      )}

      <div className="text-xs text-white/50">
        <span className="text-white/80 font-semibold">
          {ls.type === 'list' ? (ls.leadIds?.length || 0) : '—'}
        </span>{' '}
        leads will be dialed
      </div>
    </div>
  );
}

function Step3Schedule({ form, update }) {
  const s = form.schedule;
  const setSchedule = (patch) => update({ schedule: { ...s, ...patch } });
  const toggleDay = (d) => {
    const cur = new Set(s.daysOfWeek || []);
    if (cur.has(d)) cur.delete(d); else cur.add(d);
    setSchedule({ daysOfWeek: Array.from(cur).sort() });
  };
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-5">
      <div>
        <div className="text-sm font-semibold text-white mb-1">Schedule + Compliance</div>
        <div className="text-xs text-white/50">Only call during permitted hours. Federal TCPA windows are enforced per-lead.</div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="form-label">Timezone</label>
          <input
            className="input w-full"
            value={s.timezone}
            onChange={(e) => setSchedule({ timezone: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label">Start</label>
            <input
              type="time"
              className="input w-full"
              value={s.hours.start}
              onChange={(e) => setSchedule({ hours: { ...s.hours, start: e.target.value } })}
            />
          </div>
          <div>
            <label className="form-label">End</label>
            <input
              type="time"
              className="input w-full"
              value={s.hours.end}
              onChange={(e) => setSchedule({ hours: { ...s.hours, end: e.target.value } })}
            />
          </div>
        </div>
      </div>
      <div>
        <label className="form-label">Days of week</label>
        <div className="flex gap-2">
          {dayNames.map((d, i) => (
            <button
              key={d}
              onClick={() => toggleDay(i)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${(s.daysOfWeek || []).includes(i) ? 'bg-civix-purple/20 text-white border border-civix-purple/40' : 'text-white/40 border border-white/10'}`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>
      <div className="panel p-3 flex items-start gap-3 bg-white/5">
        <Lock className="w-4 h-4 text-civix-cyan mt-0.5" />
        <div className="flex-1">
          <div className="flex items-center gap-2 text-sm text-white">
            <input
              type="checkbox"
              checked
              disabled
              title="TCPA compliance is required by federal law and cannot be disabled."
            />
            Respect TCPA hours (8am–9pm local per lead's state)
          </div>
          <div className="text-xs text-white/40 mt-1">
            Federal regulation. Cannot be disabled — calls outside this window are auto-skipped.
          </div>
        </div>
      </div>
      <div className="panel p-3 flex items-start gap-3 bg-white/5">
        <Lock className="w-4 h-4 text-civix-cyan mt-0.5" />
        <div className="flex-1">
          <div className="flex items-center gap-2 text-sm text-white">
            <input type="checkbox" checked disabled />
            Auto-skip DNC leads
          </div>
          <div className="text-xs text-white/40 mt-1">
            Internal DNC + federal/state DNC checked on every dial. Cannot be disabled.
          </div>
        </div>
      </div>
    </div>
  );
}

function Step4Budget({ form, update }) {
  const b = form.budget;
  const setBudget = (patch) => update({ budget: { ...b, ...patch } });
  const approxQualifiedPerDay = b.dailyCapUsd > 0 ? Math.floor(b.dailyCapUsd / 1.93) : 0;
  return (
    <div className="space-y-5">
      <div>
        <div className="text-sm font-semibold text-white mb-1">Budget</div>
        <div className="text-xs text-white/50">Per-call and per-day caps. Hard-stop — the engine pauses you at the cap.</div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="form-label">Per-call cap (USD)</label>
          <input
            type="number" step="0.01" min="0"
            className="input w-full"
            value={b.perCallCapUsd}
            onChange={(e) => setBudget({ perCallCapUsd: Number(e.target.value) })}
          />
        </div>
        <div>
          <label className="form-label">Daily cap (USD)</label>
          <input
            type="number" step="0.01" min="0"
            className="input w-full"
            value={b.dailyCapUsd}
            onChange={(e) => setBudget({ dailyCapUsd: Number(e.target.value) })}
          />
        </div>
      </div>
      <CostBadge
        mode="full"
        label={`Estimated cost per qualified call: $1.93`}
        spent={0}
        cap={b.dailyCapUsd}
        sub={`at $${b.dailyCapUsd.toFixed(2)} daily cap, ~${approxQualifiedPerDay} qualified calls/day`}
      />
    </div>
  );
}

function Step5Pacing({ form, team, update }) {
  const p = form.pacing;
  const h = form.handoff;
  const setPacing  = (patch) => update({ pacing: { ...p, ...patch } });
  const setHandoff = (patch) => update({ handoff: { ...h, ...patch } });
  const toggleAgent = (id) => {
    const cur = new Set(h.transferTo || []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    setHandoff({ transferTo: Array.from(cur) });
  };
  return (
    <div className="space-y-5">
      <div>
        <div className="text-sm font-semibold text-white mb-1">Pacing + Hand-off</div>
        <div className="text-xs text-white/50">How many lines run in parallel, and what happens once the bot qualifies a lead.</div>
      </div>
      <div>
        <label className="form-label">Parallel lines: <span className="text-white">{p.parallelLines}</span></label>
        <input
          type="range" min="1" max="5"
          value={p.parallelLines}
          onChange={(e) => setPacing({ parallelLines: Number(e.target.value) })}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-white/40 mt-1">
          <span>1</span><span>2</span><span>3</span><span>4</span><span>5</span>
        </div>
      </div>
      <div>
        <label className="form-label">Hand-off mode</label>
        <div className="space-y-2">
          {[
            { id: 'warm_transfer_then_book', label: 'Warm transfer if agent available, else book appointment' },
            { id: 'book_only',                label: 'Book appointment only (no warm transfer)' },
            { id: 'transfer_only',            label: 'Warm transfer only (drop call if no agent)' },
          ].map((m) => (
            <label key={m.id} className="flex items-start gap-3 p-3 rounded-lg border border-white/10 hover:bg-white/5 cursor-pointer">
              <input
                type="radio"
                checked={h.mode === m.id}
                onChange={() => setHandoff({ mode: m.id })}
                className="mt-0.5"
              />
              <span className="text-sm text-white/80">{m.label}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <label className="form-label">Transfer to (agents)</label>
        <div className="panel p-3 max-h-[160px] overflow-y-auto">
          {team.length === 0 ? (
            <div className="text-xs text-white/40">No teammates — defaults to current agent.</div>
          ) : team.map((a) => {
            const sel = (h.transferTo || []).includes(a._id);
            return (
              <label key={a._id} className="flex items-center gap-3 py-1.5 px-2 rounded-sm hover:bg-white/5 cursor-pointer text-sm">
                <input type="checkbox" checked={sel} onChange={() => toggleAgent(a._id)} />
                <span className="text-white/80">{a.name}</span>
                <span className="text-white/40 text-xs">{a.email}</span>
              </label>
            );
          })}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="form-label">Transfer timeout (sec)</label>
          <input
            type="number" min="15" max="60"
            className="input w-full"
            value={h.transferTimeoutSec}
            onChange={(e) => setHandoff({ transferTimeoutSec: Number(e.target.value) })}
          />
        </div>
        <div>
          <label className="form-label">Appointment calendar ID</label>
          <input
            className="input w-full"
            placeholder="primary"
            value={h.appointmentCalendarId}
            onChange={(e) => setHandoff({ appointmentCalendarId: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
}

function Step6Review({ form, update, nameInputRef, nameError, namePulse }) {
  const errorClasses = nameError
    ? ` border border-red-500 focus:border-red-500 focus:ring-red-500/40${namePulse ? ' animate-pulse' : ''}`
    : '';
  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm font-semibold text-white mb-1">Review + Launch</div>
        <div className="text-xs text-white/50">Confirm everything before saving.</div>
      </div>
      <div>
        <label className="form-label" htmlFor="campaign-name">Campaign name</label>
        <input
          id="campaign-name"
          ref={nameInputRef}
          className={`input w-full${errorClasses}`}
          value={form.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder="e.g. May aged-lead blast — Texas FE"
          aria-invalid={nameError || undefined}
          aria-describedby={nameError ? 'campaign-name-error' : 'campaign-name-help'}
          autoFocus
        />
        {nameError ? (
          <p id="campaign-name-error" className="text-red-400 text-xs mt-1">
            Campaign name is required to save or launch.
          </p>
        ) : (
          <div id="campaign-name-help" className="text-xs text-white/40 mt-1">
            Required. Only visible to you and your team — leads never hear this.
          </div>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-4 text-sm">
        <div className="panel p-3 space-y-1">
          <div className="text-xs uppercase tracking-wider text-white/40">Persona</div>
          <div className="text-white/80">{form.persona.name}</div>
          <div className="text-xs text-white/50">{form.persona.qualifyingQuestions?.length || 0} qualifying questions</div>
        </div>
        <div className="panel p-3 space-y-1">
          <div className="text-xs uppercase tracking-wider text-white/40">Lead source</div>
          <div className="text-white/80 capitalize">{form.leadSource.type}</div>
          {form.leadSource.type === 'list' && (
            <div className="text-xs text-white/50">{form.leadSource.leadIds?.length || 0} leads</div>
          )}
        </div>
        <div className="panel p-3 space-y-1">
          <div className="text-xs uppercase tracking-wider text-white/40">Schedule</div>
          <div className="text-white/80">{form.schedule.hours.start}–{form.schedule.hours.end} {form.schedule.timezone}</div>
          <div className="text-xs text-white/50">Days: {(form.schedule.daysOfWeek || []).length}</div>
        </div>
        <div className="panel p-3 space-y-1">
          <div className="text-xs uppercase tracking-wider text-white/40">Budget</div>
          <div className="text-white/80">${form.budget.perCallCapUsd.toFixed(2)} per call · ${form.budget.dailyCapUsd.toFixed(2)} daily</div>
        </div>
        <div className="panel p-3 space-y-1">
          <div className="text-xs uppercase tracking-wider text-white/40">Pacing</div>
          <div className="text-white/80">{form.pacing.parallelLines} parallel lines</div>
        </div>
        <div className="panel p-3 space-y-1">
          <div className="text-xs uppercase tracking-wider text-white/40">Hand-off</div>
          <div className="text-white/80 capitalize">{form.handoff.mode.replace(/_/g, ' ')}</div>
          <div className="text-xs text-white/50">{(form.handoff.transferTo || []).length} agents · {form.handoff.transferTimeoutSec}s timeout</div>
        </div>
      </div>
    </div>
  );
}
